import { atom, map } from 'nanostores';
import type { WebContainer } from '@webcontainer/api';
import { editorStore } from './editor';

/** Set when `executeBuild` stops at npm install, Vite, or `tsc --noEmit`. */
export type WorkbenchBuildFailurePhase = 'npm-install' | 'vite' | 'tsc';

export interface WorkbenchBuildFailure {
  phase: WorkbenchBuildFailurePhase;
  errors: string;
  output: string;
  at: number;
}

/** The generated app crashed inside the preview iframe (reported by `src/ideoz/renderSignal.ts`). */
export interface WorkbenchPreviewCrash {
  kind: 'runtime' | 'compile';
  message: string;
  stack?: string;
  file?: string;
  pathname: string;
  at: number;
}

/** Steps of `runPreviewRecovery` (`webcontainer/previewRecovery.ts`), in the order tried. */
export type WorkbenchPreviewRecoveryStep =
  | 'restart-dev-server'
  | 'remount-dependencies'
  | 'reboot-webcontainer';

/**
 * Why recovery started. All are environment failures: the generated code is not at fault, so
 * nothing that follows may spend credits.
 */
export type WorkbenchPreviewRecoveryReason =
  | 'dev-server-crashed'
  | 'dev-server-timeout'
  | 'preview-silent'
  /** A build or check found the WebContainer frozen (a call did not answer in time). */
  | 'workspace-unresponsive';

export interface WorkbenchPreviewRecovery {
  /** `failed`: every step failed; the UI offers "Reload". */
  status: 'idle' | 'recovering' | 'failed';
  reason?: WorkbenchPreviewRecoveryReason;
  step?: WorkbenchPreviewRecoveryStep;
  attempt?: number;
}

/**
 * Which tab runs the WebContainer (`webcontainer/tabOwnership.ts`).
 * `other-tab`: another tab holds it. `other-tab-busy`: that tab is mid-build.
 * `released`: this tab gave it to another tab and its WebContainer was torn down.
 */
export type WorkbenchTabOwnership =
  | 'unknown'
  | 'owner'
  | 'other-tab'
  | 'other-tab-busy'
  | 'released';

/** AI attempted to overwrite a path the user edited manually during streaming. */
export interface WorkbenchFileConflict {
  aiContent: string;
  userContent: string;
  /** Path the AI tool targeted (may differ from store key after ts→tsx promotion). */
  aiPath?: string;
}

export const workbenchStore = {
  webcontainer: atom<WebContainer | null>(null),

  brandLogoSrc: atom<string | undefined>(undefined),

  session: atom<{ mode: string; skills: string[] }>({ mode: 'website', skills: [] }),

  files: map<Record<string, { content: string; modified?: boolean }>>({}),

  /** Binary assets keyed by WebContainer path (e.g. public/media/hash.jpg). */
  assets: map<Record<string, { base64: string; mimeType?: string }>>({}),

  /** Last persisted tool-write content per path (for unified diff vs manual edits). Updated by executeWriteFile. */
  lastToolWrittenByPath: atom<Record<string, string>>({}),

  /** path → conflict when AI writeFile collides with `modified: true` user content. */
  fileConflicts: map<Record<string, WorkbenchFileConflict>>({}),

  /** Set when manual-edit context is capped before a build-stream POST. */
  manualContextTruncationWarning: atom<string | null>(null),

  previewUrl: atom<string>(''),


  /**
   * True after a prototype snapshot zip was applied — allows the preview iframe
   * without requiring a fresh production build (Publish still gates on buildStatus).
   */
  snapshotPreviewReady: atom<boolean>(false),

  /**
   * True once the preview iframe is genuinely showing the generated app — not just once the
   * build-stream itself finished. Set by `Workbench` from the same `bridgeReady` signal that
   * gates its own loading overlay (the injected preview-bridge checking in from inside the
   * iframe, not the iframe's native `load` event — see the comment above `previewWaitingForIframe`
   * there for why that distinction matters). Consumers outside the workbench tree — e.g. the
   * panel toolbar deciding when to reveal "View Insights" / "Publish" — read this instead of a
   * stream-completion flag, so those controls don't appear over what's still a loading screen.
   */
  previewReady: atom<boolean>(false),

  /**
   * True once the iframe reported `IDEOZ_EDIT_RENDERED` for the current page load, so the
   * generated app has elements in `#root`. Reset when the iframe remounts or the URL changes.
   */
  previewRendered: atom<boolean>(false),

  /** Set on `IDEOZ_EDIT_CRASHED`; cleared by the next `IDEOZ_EDIT_RENDERED` or an iframe remount. */
  previewCrash: atom<WorkbenchPreviewCrash | null>(null),

  /**
   * The rendered page is the starter `App.tsx` (`data-ideoz-template-placeholder`), not a
   * generated prototype. The workbench shows "No saved version" instead of it (SN-4).
   */
  previewShowsTemplatePlaceholder: atom<boolean>(false),

  buildStatus: atom<'idle' | 'building' | 'success' | 'error'>('idle'),

  /**
   * True once a manual editor edit has landed in `files`/`assets` since the last successful
   * `executeBuild()`. `buildStatus` alone can't tell a caller this — it stays `'success'` from
   * the previous build until a *new* build starts, so a hand-edit made after that build has
   * nothing to flip it. Consumers (Publish's disabled-reason) use this to warn that the
   * snapshot they'd publish now diverges from what was last actually built and verified.
   * Set `true` by the editor's `onChange`; cleared back to `false` by every path that either
   * runs a fresh successful build (`Workbench`'s Rebuild button, the build-stream tool call) or
   * replaces the whole file set outright (snapshot restore, template reset) — both make the
   * store's content match what was just built/loaded again.
   */
  hasUnbuiltEdits: atom<boolean>(false),

  isStreaming: atom<boolean>(false),

  /** True while a client tool (`writeFile`, `build`, etc.) is executing in WebContainer. */
  clientToolsBusy: atom<boolean>(false),

  depsInstalled: atom<boolean>(false),

  /** WebContainer boot lifecycle for UI (skeleton / errors). */
  bootStatus: atom<'idle' | 'booting' | 'ready' | 'error'>('idle'),
  bootError: atom<string | undefined>(undefined),

  /** Idle prewarm lifecycle (app shell schedules boot early). */
  prewarmState: atom<'idle' | 'scheduled' | 'running' | 'ready' | 'skipped' | 'failed'>('idle'),

  /** Vite dev server health inside WebContainer. */
  devServerStatus: atom<'idle' | 'starting' | 'ready' | 'crashed' | 'error'>('idle'),

  /**
   * Environment recovery lifecycle (`recoverPreviewEnvironment` in `webcontainer/index.ts`).
   * Tab-level like the WebContainer itself, so `reset()` leaves it alone.
   */
  previewRecovery: atom<WorkbenchPreviewRecovery>({ status: 'idle' }),

  /** Tab-level like `previewRecovery`, so `reset()` leaves it alone. */
  tabOwnership: atom<WorkbenchTabOwnership>('unknown'),

  /** Suppress auto-snapshot while restoring a zip into the container. */
  restoreInProgress: atom<boolean>(false),
  /**
   * True from the moment the panel opens until the tab knows whether this conversation has a
   * saved version, and through the restore of one. Wider than `restoreInProgress`, which only
   * covers the writes: the answer is still unknown while the index and the zip are downloading,
   * and until then the starter page on screen says nothing about what was saved.
   */
  snapshotRestorePending: atom<boolean>(false),

  /**
   * The founder pressed Continue on a build that did not finish, and it has not started building
   * yet (it is looking for saved files, or about to build the turn again). Until then the
   * workspace can hold only the starter page, and "No saved version of this prototype yet" was
   * wrong for a conversation with versions.
   */
  continuingBuild: atom<boolean>(false),

  /**
   * A fast snapshot switch is writing files with Vite running (P6.4d-2). The preview counts as
   * not ready meanwhile, so no page is attached to a half-written version.
   */
  previewSwitching: atom<boolean>(false),

  /**
   * The WebContainer whose files this tab's `files`/`assets` store mirrors exactly, set by a
   * finished snapshot restore. Anything that clears the store without the container clears it,
   * which sends the next restore down the full path.
   */
  filesTrustedFor: atom<object | null>(null),

  /**
   * A manual-edit save was refused: another tab saved this turn after this tab loaded it
   * (P6.4e). The workbench asks whether to load the latest or keep this tab's copy.
   */
  saveConflict: atom<{ llmInteractionId: string } | null>(null),

  /**
   * Saving the founder's edits into the builder's copy, which the next change starts from.
   * `failed` keeps a Retry on screen: an edit the builder does not have is lost on the next change.
   */
  builderSave: atom<{ status: 'saving' | 'saved' | 'failed'; llmInteractionId: string } | null>(null),

  /** Last failed production build (cleared when a new build starts). Subscribe or listen for `workbench:build-failed` / `workbench:tsc-failed`. */
  lastBuildFailure: atom<WorkbenchBuildFailure | null>(null),

  /**
   * Light preview (light-preview plan F2). The in-tab compiler publishes here: errors from the
   * last compile, packages the vendor bundle cannot serve, and whether a compile is running.
   * Empty and false in the WebContainer path.
   */
  previewCompileErrors: atom<string[]>([]),
  previewUnsupportedPackages: atom<string[]>([]),
  previewCompiling: atom<boolean>(false),
  /**
   * The light preview's last compile produced no page, so the iframe still shows an earlier set
   * of files: the starter after a reload, or the version picked before. Set whether or not the
   * errors are shown; a restore clears it until its own compile reports.
   */
  previewCompileFailed: atom<boolean>(false),

  getFiles(): [string, string][] {
    const files = workbenchStore.files.get();
    return Object.entries(files).map(([path, f]) => [path, f.content]);
  },

  /**
   * The workspace holds only the starter page (its `App.tsx` marks itself), not a prototype.
   * Saving that as a snapshot replaced a built version with the starter (testing, 30 Sep 2026: a
   * dev reload emptied the store and the files autosave uploaded it).
   */
  isStarterWorkspace(): boolean {
    const app = workbenchStore.files.get()['src/App.tsx']?.content;
    return typeof app === 'string' && app.includes('data-ideoz-template-placeholder');
  },

  getModifiedFiles(): Record<string, string> {
    const files = workbenchStore.files.get();
    const modified: Record<string, string> = {};
    for (const [path, f] of Object.entries(files)) {
      if (f.modified) modified[path] = f.content;
    }
    return modified;
  },

  /** After a Chef turn completes with no pending client tools; marks user edits consumed for next build-stream body. */
  clearManualEditConsumedFlags(): void {
    const conflicts = workbenchStore.fileConflicts.get();
    for (const [path, f] of Object.entries(workbenchStore.files.get())) {
      if (f.modified && !conflicts[path]) {
        workbenchStore.files.setKey(path, { content: f.content, modified: false });
      }
    }
  },

  setFileConflict(path: string, conflict: WorkbenchFileConflict): void {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!trimmed) return;
    workbenchStore.fileConflicts.setKey(trimmed, conflict);
  },

  clearFileConflict(path: string): void {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!trimmed) return;
    const next = { ...workbenchStore.fileConflicts.get() };
    delete next[trimmed];
    workbenchStore.fileConflicts.set(next);
  },

  getFileConflictPaths(): string[] {
    return Object.keys(workbenchStore.fileConflicts.get());
  },

  hasFileConflicts(): boolean {
    return Object.keys(workbenchStore.fileConflicts.get()).length > 0;
  },

  recordToolWriteSnapshot(path: string, content: string): void {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!trimmed) return;
    const next = { ...workbenchStore.lastToolWrittenByPath.get(), [trimmed]: content };
    workbenchStore.lastToolWrittenByPath.set(next);
  },

  removeFile(path: string): void {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!trimmed) return;

    const files = { ...workbenchStore.files.get() };
    delete files[trimmed];
    workbenchStore.files.set(files);

    const assets = { ...workbenchStore.assets.get() };
    delete assets[trimmed];
    workbenchStore.assets.set(assets);

    const snapshots = { ...workbenchStore.lastToolWrittenByPath.get() };
    delete snapshots[trimmed];
    workbenchStore.lastToolWrittenByPath.set(snapshots);

    workbenchStore.clearFileConflict(trimmed);

    const selected = editorStore.selectedFile.get();
    if (selected === trimmed) {
      editorStore.selectedFile.set(null);
    }
  },

  /** Baseline body after last Chef/tool write — used only for computing manual-edit diffs. */
  getLastToolWritten(path: string): string | undefined {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    return workbenchStore.lastToolWrittenByPath.get()[trimmed];
  },

  reset(): void {
    /** Logs stay until the user clears them — resetting other workbench state runs often on effect cleanup. */
    workbenchStore.brandLogoSrc.set(undefined);
    workbenchStore.session.set({ mode: 'website', skills: [] });
    workbenchStore.files.set({});
    workbenchStore.assets.set({});
    workbenchStore.lastToolWrittenByPath.set({});
    workbenchStore.fileConflicts.set({});
    workbenchStore.manualContextTruncationWarning.set(null);
    workbenchStore.previewUrl.set('');
    workbenchStore.snapshotPreviewReady.set(false);
    workbenchStore.previewReady.set(false);
    workbenchStore.previewRendered.set(false);
    workbenchStore.previewCrash.set(null);
    workbenchStore.previewShowsTemplatePlaceholder.set(false);
    workbenchStore.buildStatus.set('idle');
    workbenchStore.isStreaming.set(false);
    workbenchStore.clientToolsBusy.set(false);
    workbenchStore.bootStatus.set('idle');
    workbenchStore.bootError.set(undefined);
    workbenchStore.prewarmState.set('idle');
    workbenchStore.devServerStatus.set('idle');
    workbenchStore.restoreInProgress.set(false);
    workbenchStore.snapshotRestorePending.set(false);
    workbenchStore.continuingBuild.set(false);
    workbenchStore.previewSwitching.set(false);
    workbenchStore.filesTrustedFor.set(null);
    workbenchStore.saveConflict.set(null);
    workbenchStore.builderSave.set(null);
    workbenchStore.lastBuildFailure.set(null);
    workbenchStore.previewCompileErrors.set([]);
    workbenchStore.previewUnsupportedPackages.set([]);
    workbenchStore.previewCompiling.set(false);
    workbenchStore.previewCompileFailed.set(false);
    editorStore.selectedFile.set(null);
    editorStore.scrollPosition.set({ line: 0, ch: 0 });
  },
};
